import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../store/index';
import { apiGetMineInfo, apiGetPage, apiGetCustom, apiGetApplicationDetail } from '../request/api'
import { imageUrl, zeusApp } from '../request/index'
import { LogoutOutlined, SwapRightOutlined } from '@ant-design/icons';
import '../assets/css/header.css'
import logo from '../assets/image/logo-white.png';

interface OrderItem {
    name: string;
    url: string;
}

interface ApiPageRow {
    mainLand: string;
    siteName: string;
    siteLanguage: string;
    siteCode: string;
}

interface TransformedLanguageList {
    areaNameEn: string;
    areaNameZh:string;
    items: {
        nameZh:string;
        nameEn:string;
        siteCode: string;
        languageList: { labelZh: string; labelEn: string; value: string }[];
        isClose: boolean;
    }[];
}

// 定义多语言导航菜单
const navListByLanguage = {
    zh: [
        { name: '奈尔简介', url: '/home' },
        { name: '专业领域', url: '/service' },
        { name: '人员概况', url: '/contact' },
        { name: '全球网络', url: '/about' },
        { name: '新闻资讯', url: '/news' }
    ],
    en: [
        { name: 'About Us', url: '/home' },
        { name: 'Practice Areas', url: '/service' },
        { name: 'Our Team', url: '/contact' },
        { name: 'Global Network', url: '/about' },
        { name: 'News', url: '/news' }
    ]
};

const Header: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const logoImage = localStorage.getItem('logoImage')
    const themeColor = '#ac9455';
    const [isLanguageVisible, setIsLanguageVisible] = useState<boolean>(false);
    const [hoverIndex, setHoverIndex] = useState<number | null>(null);
    const dispatch = useDispatch();
    const [hoveredIndex, setHoveredIndex] = useState<{ regionIndex: number | null, countryIndex: number | null }>({
        regionIndex: null,
        countryIndex: null
    });
    const [transformedList, setTransformedList] = useState<TransformedLanguageList[]>([]);
    const [loginState, setLoginState] = useState(0)
    const [isLanguageHover, setIsLanguageHover] = useState<boolean>(false);
    const [isUserHover, setIsUserHover] = useState<boolean>(false);
    const [isUserIconHovered, setIsUserIconHovered] = useState(false);
    
const languageCode = localStorage.getItem('siteLanguage') || 'zh';
    const [navList, setNavList] = useState<OrderItem[]>(navListByLanguage[languageCode as keyof typeof navListByLanguage]);
    
    const intervalRef = React.useRef<NodeJS.Timeout | null>(null);
    const [selectedLanguageDisplay, setSelectedLanguageDisplay] = useState<string>(
        languageCode === 'zh' ? 'CN-zh' : 'CN-en'
    );
    
    const [hoveredLanguageItem, setHoveredLanguageItem] = useState<{
        regionIndex: number | null;
        countryIndex: number | null;
        itemIndex: number | null;
    }>({
        regionIndex: null,
        countryIndex: null,
        itemIndex: null
    });

    // 本地语言数据
    const localLanguageData: TransformedLanguageList[] = [
    {
        areaNameZh: '亚太地区',
        areaNameEn: 'Asia Pacific',
        items: [
            {
                nameZh: '中国',
                nameEn: 'China',
                siteCode: 'CN',
                languageList: [
                    { labelZh: '中文', labelEn: '中文', value: 'zh' },
                    { labelZh: 'English', labelEn: 'English', value: 'en' }
                ],
                isClose: true
            }
        ]
    }
];

    useEffect(() => {
        fetchUserInfo();
        apiGetApplicationDetail('APPLICATION_LOGO').then(res => {
            localStorage.setItem('logoImage', res.data.data)
        })
        
        // 设置本地语言数据
        setTransformedList(localLanguageData);
        
        // 设置循环定时器，每隔一段时间获取用户信息
        intervalRef.current = setInterval(() => {
            fetchUserInfo();
        }, 60000); // 每 60 秒执行一次

        // 清理定时器，避免组件卸载时内存泄漏
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [dispatch]);

    // 监听语言变化，更新导航菜单
    useEffect(() => {
        setNavList(navListByLanguage[languageCode as keyof typeof navListByLanguage]);
        setSelectedLanguageDisplay(languageCode === 'zh' ? 'CN-zh' : 'CN-en');
    }, [languageCode]);

    const fetchUserInfo = () => {
        apiGetMineInfo().then(res => {
            if (res.data.data !== null) {
                setLoginState(1); // 如果 token 有效，设置登录状态
            } else {
                setLoginState(0); // 如果 token 无效，设置未登录状态
            }
        }).catch(err => {
            console.error('Error fetching user info:', err);
            setLoginState(0); // 请求失败时，也设置为未登录状态
        });
    };

    const handleMouseEnter = (index: number) => {
        setHoverIndex(index);
    };

    const handleMouseLeave = () => {
        setHoverIndex(null);
    };

    const handleNavigation = (path: string) => {
        navigate(path, { state: { resetView: true } });
    };

    const toMine = () => {
        if (loginState) {
            navigate('/mine'); // 如果有 token，导航到 /mine
        } else {
            navigate('/login'); // 如果没有 token，导航到 /login
        }
    }

    const showLanguage = () => {
        setIsLanguageVisible(!isLanguageVisible);
    }

    // 处理语言切换
    const handleLanguageChange = (langValue: string) => {
    console.log(langValue);
    localStorage.setItem('siteLanguage', langValue);
    setIsLanguageVisible(false);

    // 更新导航菜单
    setNavList(navListByLanguage[langValue as keyof typeof navListByLanguage]);
    setSelectedLanguageDisplay(langValue === 'zh' ? 'CN-zh' : 'CN-en');

    // 页面刷新，使所有组件重新读取语言设置
    window.location.reload();
};

    return (
        <div className="ezheader">
            <div className='ezheader-inner'>
                <div className='ezheader-padding'>
                    <div className='header_image' onClick={() => { navigate('/index'); setIsLanguageVisible(false) }}>
                        <img src={logo} />
                    </div>
                    <div className='header_nav'>
                        {navList.map((item, index) => (
                            <div className="nav_item"
                                key={index}
                                onClick={() => { setIsLanguageVisible(false); handleNavigation(item.url) }}
                                onMouseEnter={() => handleMouseEnter(index)}
                                onMouseLeave={handleMouseLeave}
                                style={{
                                    color: location.pathname === item.url || hoverIndex === index ? themeColor : '#fff',
                                    cursor: 'pointer',
                                    fontSize: languageCode === 'zh' ? '16px' : '18px',
                                }}
                            >
                                {item.name}
                            </div>
                        ))}
                    </div>
                    <div className='header_right'>
                        <div
                            className='header_language'
                            onClick={() => showLanguage()}
                        >
                            <i className="iconfont icon-international"
                                onMouseEnter={() => setIsLanguageHover(true)}
                                onMouseLeave={() => setIsLanguageHover(false)}
                                style={{ color: isLanguageVisible || isLanguageHover ? themeColor : 'inherit', fontSize: '22px' }}
                            />
                        </div>
                        <div
                            className='header_language_txt'
                            onMouseEnter={() => setIsLanguageHover(true)}
                            onMouseLeave={() => setIsLanguageHover(false)}
                            onClick={() => showLanguage()}
                            style={{ color: isLanguageVisible || isLanguageHover ? themeColor : '#fff' }}
                        >
                            {selectedLanguageDisplay}
                        </div>
                    </div>
                </div>
                {isLanguageVisible &&
                    <div className='language'>
                        <div className='contury_body_item'>
                            {transformedList.map((area, regionIndex) => (
    <div key={regionIndex} className="language_item">
        <div className='language_header'>
            {languageCode === 'zh' ? area.areaNameZh : area.areaNameEn}
        </div>
        {area.items.map((country, countryIndex) => (
            <div
                className='contury_item'
                key={countryIndex}
                onMouseEnter={() => setHoveredIndex({ regionIndex, countryIndex })}
                onMouseLeave={() => setHoveredIndex({ regionIndex: null, countryIndex: null })}
                style={{
                    backgroundColor:
                        hoveredIndex.regionIndex === regionIndex && hoveredIndex.countryIndex === countryIndex
                            ? '#f6f6f8'
                            : '#ffffff',
                    transition: 'background-color 0.3s ease'
                }}
            >
                <div className='contury_name'>
                    {languageCode === 'zh' ? country.nameZh : country.nameEn}
                </div>
                <div className='contury_body_item'>
                    {country.languageList.map((lang, langIndex) => (
                        <div
                            key={langIndex}
                            onMouseEnter={() =>
                                setHoveredLanguageItem({
                                    regionIndex,
                                    countryIndex,
                                    itemIndex: langIndex,
                                })
                            }
                            onMouseLeave={() =>
                                setHoveredLanguageItem({
                                    regionIndex: null,
                                    countryIndex: null,
                                    itemIndex: null,
                                })
                            }
                            onClick={() => handleLanguageChange(lang.value)}
                            style={{
                                color:
                                    hoveredLanguageItem.regionIndex === regionIndex &&
                                        hoveredLanguageItem.countryIndex === countryIndex &&
                                        hoveredLanguageItem.itemIndex === langIndex
                                        ? themeColor
                                        : '#33364b',
                                cursor: 'pointer',
                                transition: 'color 0.3s ease',
                                padding: '4px 16px 0 0',
                            }}
                        >
                            {languageCode === 'zh' ? lang.labelZh : lang.labelEn}
                        </div>
                    ))}
                </div>
            </div>
        ))}
    </div>
))}
                        </div>
                    </div>
                }
            </div>
            {isLanguageVisible &&
                <div
                    className="language_overlay"
                    onClick={() => { setIsLanguageVisible(false) }} // 点击遮罩层隐藏语言栏
                ></div>
            }
        </div>
    );
};

export default Header;